import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {Box, Stack, Typography} from '@mui/material';
import {DataGridPremium as DataGrid} from '@mui/x-data-grid-premium';
import {array, bool, func, number, object} from 'prop-types';
import React from 'react';
import AreaIndicator from '../common/AreaIndicator.jsx';
import ZoomToButton from '@components/common/ZoomToButton';

const FeaturesGrid = ({
  features,
  estimates,
  rowSelectionModel,
  onRowSelectionModelChange,
  onZoomToClicked,
  disableSelection = false,
  hideSelection = false,
  minCost = 0,
  collection,
}) => {
  const columns = [
    {
      field: 'properties.title',
      headerName: 'Title',
      flex: 1,
      valueGetter: (value, row) =>
        row.properties[collection?.title_property] || row.properties.title,
      renderCell: ({value}) => {
        if (value) {
          return <div>{value}</div>;
        } else {
          return (
            <Box
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              Untitled Feature
            </Box>
          );
        }
      },
    },
    {
      field: 'properties.pl:area',
      headerName: 'Area',
      flex: 1,
      valueGetter: (value, row) => row.properties['pl:area'],
      renderCell: ({value}) => <AreaIndicator squareMeters={value} />,
    },
    {
      field: 'quota_cost',
      headerName: 'Quota cost',
      flex: 1,
      valueGetter: (value, row) =>
        estimates[row.properties['pl:ref']]?.cost || 0,

      renderCell: ({row, value}) => {
        return (
          <Stack
            direction="row"
            sx={{gap: 1, alignItems: 'center', height: '100%'}}
          >
            {row.properties['pl:area'] < minCost && (
              <WarningAmberOutlinedIcon fontSize="small" color="action" />
            )}
            <Typography variant="body2">
              <AreaIndicator squareMeters={value} />
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 1,
      sortable: false,
      getActions: ({row}) => [
        <ZoomToButton
          data-testid="zoom-button"
          key="zoomTo"
          onClick={() => onZoomToClicked(row)}
        />,
      ],
    },
  ];

  return (
    <DataGrid
      checkboxSelection={!hideSelection}
      disableRowGrouping
      disableAggregation
      getRowId={row => row.properties['pl:ref']}
      onRowSelectionModelChange={onRowSelectionModelChange}
      rowSelectionModel={rowSelectionModel}
      columns={columns}
      rows={features}
      isRowSelectable={() => hideSelection || !disableSelection}
      disableMultipleRowSelection={hideSelection || disableSelection}
      sx={theme => ({
        '--DataGrid-rowBorderColor': (theme.vars || theme).palette.divider,
        '.MuiDataGrid-withBorderColor': {
          borderColor: (theme.vars || theme).palette.divider,
        },
        '--DataGrid-containerBackground': (theme.vars || theme).palette
          .grey[100],
        ...theme.applyStyles('dark', {
          '--DataGrid-containerBackground': '#23343F',
        }),
      })}
      data-testid="features-grid"
    />
  );
};

FeaturesGrid.propTypes = {
  features: array,
  estimates: object,
  rowSelectionModel: array,
  onRowSelectionModelChange: func,
  onZoomToClicked: func,
  disableSelection: bool,
  hideSelection: bool,
  minCost: number,
  collection: object,
};

export default FeaturesGrid;
